import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Phone, Mail, MapPin, IndianRupee, Send } from "lucide-react";
import { format, parseISO } from "date-fns";
import NotifyClientDialog from "@/components/NotifyClientDialog";
import ClientCredentials from "@/components/ClientCredentials";
import ClientLedger from "@/components/ClientLedger";

export default function ClientDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [notifyOpen, setNotifyOpen] = useState(false);

  useEffect(() => {
    api.get(`/clients/${id}`).then(({ data }) => setC(data));
    api.get(`/tasks?client_id=${id}`).then(({ data }) => setTasks(data));
  }, [id]);

  if (!c) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const pending = tasks.filter(t => t.status !== "Completed" && t.status !== "Cancelled");
  const completed = tasks.filter(t => t.status === "Completed");

  return (
    <div className="space-y-5" data-testid="client-detail-page">
      <Link to="/clients" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Back to clients
      </Link>

      <div className="flex items-start gap-5">
        <div className="w-16 h-16 rounded-lg bg-blue-600 text-white grid place-items-center font-display font-bold text-2xl">
          {c.client_name[0]}
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">{c.client_code}</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{c.client_name}</h1>
          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
            <span>{c.constitution}</span>
            <Badge variant="outline">{c.status}</Badge>
            {c.state && <span>· {c.state}</span>}
          </div>
        </div>
        {(c.mobile || c.email) && (
          <Button data-testid="client-notify-btn" onClick={() => setNotifyOpen(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Send className="w-4 h-4 mr-1.5" /> Notify Client
          </Button>
        )}
      </div>

      <NotifyClientDialog
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        client={c}
        reason="manual"
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">PAN</div><div className="font-mono text-sm mt-1">{c.pan || "—"}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">GSTIN</div><div className="font-mono text-sm mt-1">{c.gstin || "—"}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">TAN</div><div className="font-mono text-sm mt-1">{c.tan || "—"}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Turnover</div><div className="font-mono text-sm mt-1">{c.annual_turnover ? `₹${(c.annual_turnover / 100000).toFixed(1)}L` : "—"}</div></Card>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks ({tasks.length})</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Ledger</TabsTrigger>
          <TabsTrigger value="credentials" data-testid="tab-credentials">Portal Logins</TabsTrigger>
          <TabsTrigger value="contact" data-testid="tab-contact">Contact</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-5">
              <h3 className="font-display font-semibold mb-3">Pending Work ({pending.length})</h3>
              <div className="space-y-2">
                {pending.length === 0 && <div className="text-xs text-muted-foreground">Nothing pending.</div>}
                {pending.map(t => (
                  <div key={t.id} className="flex items-center gap-2 py-1.5 border-b border-border last:border-0">
                    <div className={`status-dot ${t.status === "In Progress" ? "bg-blue-500" : "bg-amber-500"}`} />
                    <div className="text-sm flex-1 truncate">{t.title}</div>
                    <div className="text-[10px] font-mono text-muted-foreground">{t.due_date ? format(parseISO(t.due_date), "dd MMM") : ""}</div>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="p-5">
              <h3 className="font-display font-semibold mb-3">Completed Work ({completed.length})</h3>
              <div className="space-y-2">
                {completed.length === 0 && <div className="text-xs text-muted-foreground">Nothing completed yet.</div>}
                {completed.map(t => (
                  <div key={t.id} className="flex items-center gap-2 py-1.5 border-b border-border last:border-0">
                    <div className="status-dot bg-emerald-500" />
                    <div className="text-sm flex-1 truncate">{t.title}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="tasks" className="mt-4">
          <Card className="p-5">
            <div className="space-y-2">
              {tasks.map(t => (
                <div key={t.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                  <Badge variant="outline" className="text-[10px]">{t.department}</Badge>
                  <div className="text-sm flex-1">{t.title}</div>
                  <Badge className="text-[10px]">{t.status}</Badge>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="ledger" className="mt-4">
          <ClientLedger clientId={id} clientName={c.client_name} clientMobile={c.mobile} clientEmail={c.email} />
        </TabsContent>

        <TabsContent value="credentials" className="mt-4">
          <ClientCredentials clientId={id} />
        </TabsContent>

        <TabsContent value="contact" className="mt-4">
          <Card className="p-5 space-y-3 text-sm">
            {c.mobile && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-muted-foreground" /> <span className="font-mono">{c.mobile}</span></div>}
            {c.email && <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-muted-foreground" /> <span className="font-mono">{c.email}</span></div>}
            {c.address && <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-muted-foreground mt-0.5" /> <span>{c.address}, {c.state}</span></div>}
            {c.annual_turnover != null && <div className="flex items-center gap-2"><IndianRupee className="w-4 h-4 text-muted-foreground" /> <span className="font-mono">₹{c.annual_turnover.toLocaleString("en-IN")}</span></div>}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
